/**
 * Composable for global confirmation dialogs
 *
 * Provides a singleton confirmation modal that can be triggered from anywhere.
 * Uses module-level state so all consumers share the same dialog instance.
 *
 * @example
 * ```typescript
 * const { confirmDialog, handleConfirm, handleCancel } = useConfirm()
 *
 * // Simple confirmation
 * confirmDialog('Are you sure you want to delete this item?', () => {
 *   deleteItem()
 * })
 *
 * // With custom options
 * confirmDialog('Delete permanently?', onDelete, {
 *   headLine: 'Confirm Deletion',
 *   confirmText: 'Delete',
 *   icon: mdiDelete
 * })
 * ```
 */
import { ref } from 'vue'
import Actions from '@/constants/actions'
import type { ConfirmState, ConfirmOptions, ConfirmDialogOptions } from '@/types/confirm.types'

// ============ MODULE-LEVEL SINGLETON STATE ============

/**
 * Singleton state shared across all useConfirm() calls
 */
const state = ref<ConfirmState>({
  show: false,
  actionType: Actions.CancelConfirm,
  sectionTag: 'global-confirmation-modal'
})

/**
 * Module-level callbacks (must be module-level for singleton pattern)
 */
let confirmCallback: (() => void) | undefined
let cancelCallback: (() => void) | undefined
let closeCallback: (() => void) | undefined

// ============ COMPOSABLE ============

export function useConfirm() {
  /**
   * Show the confirmation dialog with full options
   */
  function showConfirm(options: ConfirmOptions): void {
    const { onConfirm, onCancel, onClose, ...confirmOptions } = options

    // Store callbacks at module level
    confirmCallback = onConfirm
    cancelCallback = onCancel
    closeCallback = onClose

    state.value = {
      ...confirmOptions,
      show: true
    }
  }

  /**
   * Hide the confirmation dialog
   */
  function hideConfirm(): void {
    state.value.show = false
    // Clear callbacks to prevent memory leaks
    confirmCallback = undefined
    cancelCallback = undefined
    closeCallback = undefined
  }

  /**
   * Handle confirm button click
   * Calls the confirm callback if provided, then hides the dialog
   */
  function handleConfirm(): void {
    const callback = confirmCallback
    hideConfirm()
    if (callback) {
      callback()
    }
  }

  /**
   * Handle cancel button click
   * Calls the cancel callback if provided, then hides the dialog
   */
  function handleCancel(): void {
    const callback = cancelCallback
    hideConfirm()
    if (callback) {
      callback()
    }
  }

  /**
   * Handle dialog close (backdrop click or escape key)
   * Calls the close callback if provided, then hides the dialog
   */
  function handleClose(): void {
    const callback = closeCallback
    hideConfirm()
    if (callback) {
      callback()
    }
  }

  /**
   * Convenience method for simple confirmation dialogs (callback-based)
   *
   * @param bodyText - The confirmation message
   * @param onConfirm - Callback when user confirms
   * @param options - Additional options (headLine, icon, etc.)
   * @deprecated Use confirmDialogAsync for promise-based confirmations
   */
  function confirmDialogCallback(
    bodyText: string,
    onConfirm?: () => void,
    options: Partial<ConfirmState> = {}
  ): void {
    showConfirm({
      actionType: Actions.CancelConfirm,
      bodyText,
      onConfirm,
      ...options
    })
  }

  /**
   * Unified confirmation dialog supporting both callback and promise patterns
   *
   * @overload Callback-based (legacy)
   * @param bodyText - The confirmation message (supports HTML)
   * @param onConfirm - Callback when user confirms
   * @param options - Additional options (headLine, icon, etc.)
   *
   * @overload Promise-based (recommended)
   * @param options - Dialog options (title, message, confirmText, confirmColor)
   * @returns Promise<boolean> - true if confirmed, false if cancelled
   *
   * @example Callback-based
   * ```typescript
   * confirmDialog('Are you sure?', () => deleteItem(), {
   *   headLine: 'Delete Item',
   *   icon: mdiAlertCircle
   * })
   * ```
   *
   * @example Promise-based
   * ```typescript
   * const confirmed = await confirmDialog({
   *   title: 'Delete Item',
   *   message: 'Are you sure you want to delete this item?',
   *   confirmText: 'Delete',
   *   confirmColor: 'error'
   * })
   * if (confirmed) {
   *   await deleteItem()
   * }
   * ```
   */
  function confirmDialog(
    optionsOrBodyText: ConfirmDialogOptions | string,
    onConfirm?: () => void,
    legacyOptions: Partial<ConfirmState> = {}
  ): Promise<boolean> | void {
    // Detect which pattern is being used
    if (typeof optionsOrBodyText === 'string') {
      // Legacy callback-based pattern
      showConfirm({
        actionType: Actions.CancelConfirm,
        bodyText: optionsOrBodyText,
        onConfirm,
        ...legacyOptions
      })
      return // void for callback pattern
    }

    // Promise-based pattern
    const options = optionsOrBodyText
    return new Promise(resolve => {
      showConfirm({
        actionType: Actions.CancelConfirm,
        headLine: options.title,
        bodyText: options.message,
        confirmText: options.confirmText ?? 'Confirm',
        confirmColor: options.confirmColor ?? 'primary',
        cancelText: options.cancelText ?? 'Cancel',
        icon: options.icon,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
        onClose: () => resolve(false)
      })
    })
  }

  return {
    /** Reactive dialog state */
    state,
    /** Show dialog with full options */
    showConfirm,
    /** Hide the dialog */
    hideConfirm,
    /** Handle confirm button click */
    handleConfirm,
    /** Handle cancel button click */
    handleCancel,
    /** Handle dialog close */
    handleClose,
    /** Unified confirmation dialog - supports both callback and promise patterns */
    confirmDialog,
    /** @deprecated Use confirmDialog instead */
    confirmDialogCallback
  }
}
